
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import Image from '@/components/AppImage/AppImage';
import clsx from 'clsx';
import createListWhereHero from '@/assets/images/create-list/create-list-where-hero.png';
import iconPlaces from '@/assets/images/create-list/create-list-icon-places.svg';
import iconListName from '@/assets/images/create-list/create-list-icon-list-name.svg';
import iconCategories from '@/assets/images/create-list/create-list-icon-categories.svg';
import { createOrUpdateListPost } from '@/api/list';
import type { CitySearchResult, PrimaryDestination } from '@/types/city';
import { getFirstPhotoUrl } from '@/utils/photoUtils';
import {
  buildListCityPayload,
  primaryLabelFromDestination,
} from '@/utils/createModalDestination';
import { TRAVEL_WITH_TYPES_UI, TRIP_TYPES_UI } from '@/constants/tripTypes';
import { preferencePillClass, preferencePillStyle } from '@/constants/preferencePillClass';
import { toast } from 'react-toastify';
import { debug } from '@/utils/logger';
import SVGSpinner from '@/components/svgs/SVGSpinner';
import WhereDestinationPicker from '@/components/WhereDestinationPicker/WhereDestinationPicker';
import ResponsiveOverlay from '@/components/ResponsiveOverlay';
import { useCreateModalSheetLayout } from '@/hooks/useCreateModalSheetLayout';
import { blurActiveEditableElement } from '@/utils/keyboardInset';
import './CreateListModal.css';

/** Proxy photo width for hero (dropdown thumbnails use 80). */
const CITY_HERO_PHOTO_SIZE = 720;

/** Exactly one of the three modal "selector" rows is visually active (Figma). */
type ModalSelector = 'where' | 'listName' | 'categories';

interface Props {
  onClose: () => void;
  /** Called after the list is created, before navigating to `/list/[id]`. Use to clear parent overlay state (e.g. pancake menu). */
  onListCreated?: () => void;
  /**
   * Tab hint for Add Places after creation. Append `?addMode=<tab>` to the post-create navigation
   * so /list/[id] auto-opens its Add Place modal on the specified tab.
   * Pass 'copy-paste' for paste-text import, 'search' for explore, or true (legacy, → copy-paste).
   */
  openAddModeOnCreate?: 'copy-paste' | 'search' | 'social' | boolean;
}

const CreateListModal: React.FC<Props> = ({ onClose, onListCreated, openAddModeOnCreate = false }) => {
  const router = useNavigate();

  const [selectedDestination, setSelectedDestination] = useState<PrimaryDestination | null>(null);
  const [selectedCityRaw, setSelectedCityRaw] = useState<CitySearchResult | null>(null);
  const [selectedDestinationPhotoUrl, setSelectedDestinationPhotoUrl] = useState<string | undefined>();

  const [listName, setListName] = useState('');

  const [selectedTripTypes, setSelectedTripTypes] = useState<string[]>([]);
  const [selectedTravelWithTypes, setSelectedTravelWithTypes] = useState<string[]>([]);
  const [pendingTripTypes, setPendingTripTypes] = useState<string[]>([]);
  const [pendingTravelWithTypes, setPendingTravelWithTypes] = useState<string[]>([]);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);

  const [activeSelector, setActiveSelector] = useState<ModalSelector>('where');

  const [isSubmitting, setIsSubmitting] = useState(false);

  const listNameInputRef = useRef<HTMLInputElement>(null);
  const destinationContainerRef = useRef<HTMLDivElement>(null);
  const { setScrollRef } = useCreateModalSheetLayout();

  const handleClose = useCallback(() => {
    blurActiveEditableElement();
    onClose();
  }, [onClose]);

  const listNameFromCityRef = useRef<string | null>(null);

  const cityHeroUrl = useMemo(() => {
    if (selectedDestinationPhotoUrl) return selectedDestinationPhotoUrl;
    if (!selectedCityRaw) return '';
    return (
      getFirstPhotoUrl(
        {
          photos: selectedCityRaw.photos as
            | Array<{ name?: string; photo_reference?: string }>
            | undefined,
        },
        CITY_HERO_PHOTO_SIZE
      ) || ''
    );
  }, [selectedCityRaw, selectedDestinationPhotoUrl]);

  const [cityHeroReveal, setCityHeroReveal] = useState(false);
  const [cityHeroFailed, setCityHeroFailed] = useState(false);

  useEffect(() => {
    setCityHeroReveal(false);
    setCityHeroFailed(false);
  }, [cityHeroUrl]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (isCategoryOpen) {
        e.preventDefault();
        setPendingTripTypes([...selectedTripTypes]);
        setPendingTravelWithTypes([...selectedTravelWithTypes]);
        setIsCategoryOpen(false);
        setActiveSelector('where');
        return;
      }
      handleClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleClose, isCategoryOpen, selectedTravelWithTypes, selectedTripTypes]);

  const applyAutoListName = useCallback((dest: PrimaryDestination) => {
    const label = primaryLabelFromDestination(dest);
    const suggestedListName = label.length > 0 ? `${label} spots` : '';
    if (!suggestedListName) return;

    setListName((prev) => {
      const prevTrimmed = prev.trim();
      const stillAutoSuggested =
        listNameFromCityRef.current !== null && prev === listNameFromCityRef.current;
      if (prevTrimmed === '' || stillAutoSuggested) {
        listNameFromCityRef.current = suggestedListName;
        return suggestedListName;
      }
      return prev;
    });
  }, []);

  const handleDestinationChange = useCallback(
    (dest: PrimaryDestination | null) => {
      setSelectedDestination(dest);
      if (dest) {
        applyAutoListName(dest);
        debug('[CreateListModal] Destination selected', {
          type: dest.type,
          identifyingName: dest.identifying_name,
        });
      }
    },
    [applyAutoListName],
  );

  const handleCityChange = useCallback((city: CitySearchResult | null) => {
    setSelectedCityRaw(city);
  }, []);

  const handleDestinationPhotoChange = useCallback((photoUrl: string | undefined) => {
    setSelectedDestinationPhotoUrl(photoUrl);
  }, []);

  const handleListNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setListName(v);
    if (listNameFromCityRef.current !== null && v !== listNameFromCityRef.current) {
      listNameFromCityRef.current = null;
    }
  }, []);

  const discardCategoryDrawer = useCallback(() => {
    setPendingTripTypes([...selectedTripTypes]);
    setPendingTravelWithTypes([...selectedTravelWithTypes]);
    setIsCategoryOpen(false);
  }, [selectedTravelWithTypes, selectedTripTypes]);

  const handleWherePointerDown = useCallback((e: React.PointerEvent) => {
    discardCategoryDrawer();
    setActiveSelector('where');
    const target = e.target as HTMLElement;
    if (target.closest('input, textarea, select, button, [role="tab"], [role="combobox"]')) {
      return;
    }
    destinationContainerRef.current?.querySelector('input')?.focus();
  }, [discardCategoryDrawer]);

  const handleListNameAreaPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const el = e.target as HTMLElement;
    if (el.closest('[data-clm-clear]')) return;
    discardCategoryDrawer();
    setActiveSelector('listName');
    if (el.closest('input, textarea')) return;
    listNameInputRef.current?.focus();
  }, [discardCategoryDrawer]);

  const handleCategoriesTriggerClick = useCallback(() => {
    if (isCategoryOpen) {
      discardCategoryDrawer();
      setActiveSelector('where');
      return;
    }
    setPendingTripTypes([...selectedTripTypes]);
    setPendingTravelWithTypes([...selectedTravelWithTypes]);
    setIsCategoryOpen(true);
    setActiveSelector('categories');
  }, [discardCategoryDrawer, isCategoryOpen, selectedTravelWithTypes, selectedTripTypes]);

  const handleCategorySkip = useCallback(() => {
    discardCategoryDrawer();
    setActiveSelector('where');
  }, [discardCategoryDrawer]);

  // Committing nothing is allowed: the backend then picks the categories.
  const handleCategoryUpdate = useCallback(() => {
    setSelectedTripTypes(pendingTripTypes);
    setSelectedTravelWithTypes(pendingTravelWithTypes);
    setIsCategoryOpen(false);
    setActiveSelector('where');
  }, [pendingTravelWithTypes, pendingTripTypes]);

  const togglePendingTripType = useCallback((id: string) => {
    setPendingTripTypes((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  }, []);

  const togglePendingTravelWith = useCallback((id: string) => {
    setPendingTravelWithTypes((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  }, []);

  // Categories are optional: when skipped, the backend fills them from the profile, then AI.
  const isStartEnabled =
    selectedDestination !== null &&
    listName.trim().length > 0;

  const handleStart = useCallback(async () => {
    if (!isStartEnabled || isSubmitting || !selectedDestination) return;

    setIsSubmitting(true);
    try {
      const listCity = buildListCityPayload(selectedDestination, selectedCityRaw);

      const payload: Record<string, unknown> = {
        post_type: 'List',
        name: listName.trim(),
        body: listName.trim(),
        is_private: true,
        is_draft: false,
        postLocations: [],
        tag_list: selectedTripTypes,
        travel_with_types: selectedTravelWithTypes,
        budget: null,
        primary_destination: selectedDestination,
        ...(listCity ? { city: listCity } : {}),
        ...(cityHeroUrl ? { image_urls: [cityHeroUrl] } : {}),
      };

      debug('[CreateListModal] Creating list', payload);
      const post = await createOrUpdateListPost(payload);
      const dest = openAddModeOnCreate
        ? `/list/${post._id}?addMode=${typeof openAddModeOnCreate === 'string' ? openAddModeOnCreate : 'copy-paste'}`
        : `/list/${post._id}`;
      router(dest, { replace: true });
      onListCreated?.();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to create list. Please try again.';
      toast.error(message);
      debug('[CreateListModal] Create list error', err);
      setIsSubmitting(false);
    }
  }, [
    cityHeroUrl,
    isStartEnabled,
    isSubmitting,
    listName,
    onListCreated,
    selectedDestination,
    selectedCityRaw,
    selectedTravelWithTypes,
    selectedTripTypes,
    router,
    openAddModeOnCreate,
  ]);

  const labelTextCls = 'font-manrope text-[14px] font-semibold leading-[1.4286] tracking-label text-gray-900';
  const entryRowLayout =
    'flex flex-col p-4 border-2 rounded-tag transition-[background-color,border-color,box-shadow] duration-150 ease-in-out';
  const entryRowIdle = 'bg-white/45 border-transparent';
  const entryRowSelected =
    'bg-[#dfefff] border-gray-900 shadow-[0_1px_0_rgba(0,0,0,0.04)]';

  const hasPreferenceSummary =
    selectedTripTypes.length > 0 || selectedTravelWithTypes.length > 0;

  return (
    <>
      <ResponsiveOverlay
        onClose={handleClose}
        presentation="sheet"
        unwrapped
        showSheetHandle
        className="clm-backdrop-enter clm-create-modal-overlay"
        aria-label="Create List"
      >
        <div
          className="clm-modal-panel clm-modal-enter gt-keyboard-sheet relative w-full min-w-0 max-w-[515px] max-md:max-w-none rounded-card max-md:rounded-t-[20px] max-md:rounded-b-none bg-gray-50 overflow-visible shadow-surface font-manrope"
        >
        <div className="clm-hero-clip" aria-hidden>
          <div className="clm-hero" aria-hidden>
            <div className="clm-hero-image-wrap">
              <Image
                src={createListWhereHero}
                alt=""
                fill
                priority
                className={clsx(
                  'clm-hero-image clm-hero-image-layer z-0',
                  cityHeroUrl && cityHeroReveal && !cityHeroFailed ? 'opacity-0' : 'opacity-100',
                )}
                sizes="(max-width: 600px) 100vw, 520px"
              />
              {cityHeroUrl ? (
                <Image
                  key={cityHeroUrl}
                  src={cityHeroUrl}
                  alt=""
                  fill
                  className={clsx(
                    'clm-hero-image clm-hero-image-layer z-[1]',
                    cityHeroReveal && !cityHeroFailed ? 'opacity-100' : 'opacity-0',
                  )}
                  sizes="(max-width: 600px) 100vw, 520px"
                  unoptimized
                  onLoadingComplete={() => setCityHeroReveal(true)}
                  onError={() => {
                    debug('[CreateListModal] City hero image failed to load', { cityHeroUrl });
                    setCityHeroFailed(true);
                  }}
                />
              ) : null}
            </div>
            <div className="clm-hero-warm" aria-hidden />
            <div className="clm-hero-fade" aria-hidden />
          </div>
        </div>

        <button
          type="button"
          className="absolute top-3.5 right-3.5 z-[5] w-10 h-10 flex items-center justify-center rounded-full bg-white/[0.92] text-gray-900 text-base leading-none shadow-[0_1px_4px_rgba(0,0,0,0.08)] border-none cursor-pointer transition-colors duration-150 hover:bg-white"
          onClick={handleClose}
          aria-label="Close"
        >
          ✕
        </button>

        <div className="clm-modal-layout">
        <div
          ref={setScrollRef}
          className="clm-modal-body relative flex flex-col gap-8 pt-[112px] px-8 pb-5 max-sm:pt-[88px] max-sm:px-5 max-sm:pb-4 max-sm:gap-6 gt-keyboard-scroll min-h-0"
        >
          <h2 className="clm-modal-title font-lora font-semibold text-[32px] leading-[1.3125] tracking-[0.015625em] text-gray-900 text-center mx-auto w-full max-w-[451px] max-sm:text-[26px]">
            Create List
          </h2>

          {openAddModeOnCreate === true || openAddModeOnCreate === 'copy-paste' ? (
            <p className="clm-modal-subtitle -mt-4 text-center mx-auto w-full max-w-[451px] font-manrope text-[14px] leading-[1.4286] text-gray-700 max-sm:text-[13px]">
              First, a few quick details about your list – you&rsquo;ll paste on the next step
            </p>
          ) : null}

          <div className="w-full min-w-0 max-w-[451px] mx-auto flex flex-col items-stretch">
            <div className="relative z-[1] w-full min-w-0 self-stretch bg-[#f2f2f2] border border-gray-200 rounded-card p-3 flex flex-col gap-2 shadow-[0_2px_14px_rgba(0,0,0,0.06)]">

              {/* Where? row */}
              <div
                className={clsx(
                  entryRowLayout,
                  activeSelector === 'where' ? entryRowSelected : entryRowIdle,
                )}
                ref={destinationContainerRef}
                onPointerDown={handleWherePointerDown}
                aria-current={activeSelector === 'where' ? 'true' : undefined}
              >
                <div className="flex flex-col justify-center gap-2 min-w-0 flex-1">
                  <div className="flex flex-row items-center gap-2 flex-wrap">
                    <span className="flex items-center justify-center shrink-0">
                      <Image src={iconPlaces} alt="" width={20} height={20} className="block w-5 h-5 shrink-0 object-contain" />
                    </span>
                    <span className={labelTextCls}>Where?</span>
                  </div>
                  <div className="pl-8 pr-2 w-full box-border" onFocusCapture={() => setActiveSelector('where')}>
                    <WhereDestinationPicker
                      value={selectedDestination}
                      onChange={handleDestinationChange}
                      onCityChange={handleCityChange}
                      onPhotoChange={handleDestinationPhotoChange}
                      embedded
                    />
                  </div>
                </div>
              </div>

              {/* List Name row */}
              <div
                className={clsx(
                  entryRowLayout,
                  'cursor-text',
                  activeSelector === 'listName' ? entryRowSelected : entryRowIdle,
                )}
                role="group"
                aria-labelledby="create-list-name-label"
                aria-current={activeSelector === 'listName' ? 'true' : undefined}
                onPointerDown={handleListNameAreaPointerDown}
              >
                <div className="flex flex-col justify-center gap-2 min-w-0 flex-1">
                  <div className="flex flex-row items-center gap-2 flex-wrap">
                    <span className="flex items-center justify-center shrink-0">
                      <Image src={iconListName} alt="" width={20} height={20} className="block w-5 h-5 shrink-0 object-contain" />
                    </span>
                    <label
                      id="create-list-name-label"
                      htmlFor="create-list-name-input"
                      className={labelTextCls}
                    >
                      List Name?
                    </label>
                  </div>
                  <div className="pl-8 pr-2 w-full box-border">
                    <div className="relative flex items-center">
                      <input
                        id="create-list-name-input"
                        ref={listNameInputRef}
                        type="text"
                        value={listName}
                        onChange={handleListNameChange}
                        onFocus={() => setActiveSelector('listName')}
                        placeholder="Give your list a name"
                        className="flex-1 min-w-0 border-none bg-transparent pr-9 font-manrope font-semibold text-base leading-[1.375] tracking-label text-gray-900 rounded-none outline-none shadow-none box-border placeholder:text-gray-500 focus:outline-none"
                        maxLength={120}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Categories row */}
              <div
                className="flex flex-col self-stretch"
                onFocusCapture={() => setActiveSelector('categories')}
              >
                <div
                  className={clsx(
                    'flex flex-col border-2 rounded-tag transition-[background-color,border-color,box-shadow] duration-150 ease-in-out',
                    activeSelector === 'categories' ? entryRowSelected : entryRowIdle,
                  )}
                  aria-current={activeSelector === 'categories' ? 'true' : undefined}
                >
                  <button
                    type="button"
                    className="w-full block text-left border-none bg-transparent p-4 cursor-pointer font-[inherit] text-[inherit] rounded-button-md transition-colors duration-[120ms] hover:bg-black/[0.03]"
                    onClick={handleCategoriesTriggerClick}
                    onFocus={() => setActiveSelector('categories')}
                    aria-expanded={isCategoryOpen}
                    aria-controls="create-list-categories-drawer"
                    aria-describedby="create-list-categories-optional"
                    id="create-list-categories-trigger"
                  >
                    <div className="w-full flex flex-col justify-center gap-2 min-w-0 flex-1">
                      <div className="flex flex-row items-center gap-2 flex-wrap">
                        <span className="flex items-center justify-center shrink-0">
                          <Image src={iconCategories} alt="" width={20} height={20} className="block w-5 h-5 shrink-0 object-contain" />
                        </span>
                        <span className={labelTextCls}>Categories</span>
                        <span id="create-list-categories-optional" className="font-manrope font-semibold text-[14px] leading-[1.4286] tracking-label text-gray-500">(optional)</span>
                      </div>
                      {!hasPreferenceSummary && !isCategoryOpen && (
                        <span className="pl-8 pr-2 font-manrope text-[13px] leading-[1.4] text-gray-500">
                          Leave empty and we&apos;ll pick them for you.
                        </span>
                      )}
                      {hasPreferenceSummary && !isCategoryOpen && (
                        <div className="pl-8 pr-2 w-full box-border">
                          <span className="flex flex-wrap gap-2">
                            {selectedTripTypes.map((id) => {
                              const type = TRIP_TYPES_UI.find((t) => t.id === id);
                              return (
                                <span
                                  key={`trip-${id}`}
                                  className="inline-flex items-center px-4 py-2 rounded-button bg-orange-100 text-gray-900 font-manrope text-[14px] font-bold leading-[1.4286] tracking-label border-none"
                                >
                                  {String(type?.label ?? id)}
                                </span>
                              );
                            })}
                            {selectedTravelWithTypes.map((id) => {
                              const tw = TRAVEL_WITH_TYPES_UI.find((t) => t.id === id);
                              return (
                                <span
                                  key={`tw-${id}`}
                                  className="inline-flex items-center px-4 py-2 rounded-button bg-orange-100 text-gray-900 font-manrope text-[14px] font-bold leading-[1.4286] tracking-label border-none"
                                >
                                  {String(tw?.label ?? id)}
                                </span>
                              );
                            })}
                          </span>
                        </div>
                      )}
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="clm-modal-footer">
            <button
              type="button"
              className={clsx(
                'relative z-0 inline-flex items-center justify-center box-border h-9 min-h-9 px-5',
                'rounded-button-md border-none font-manrope text-[14px] font-bold leading-[1.4286] tracking-label transition-colors duration-150',
                isSubmitting || isStartEnabled
                  ? 'bg-orange-500 text-white gap-2'
                  : 'bg-gray-200 text-[#75757c] cursor-not-allowed',
                isSubmitting && 'cursor-wait opacity-90',
                isStartEnabled && !isSubmitting && 'cursor-pointer hover:bg-[#e04710]',
              )}
              onClick={handleStart}
              disabled={!isStartEnabled || isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting && <SVGSpinner className="h-4 w-4 shrink-0" color="currentColor" />}
              {isSubmitting ? 'Creating…' : 'Start'}
            </button>
        </div>
        </div>
        </div>
      </ResponsiveOverlay>

      {isCategoryOpen && (
        <ResponsiveOverlay
          onClose={handleCategorySkip}
          presentation="sheet"
          className="clm-categories-overlay"
          aria-labelledby="create-list-categories-popover-title"
          panelClassName="max-w-[min(100%,480px)] gap-4 rounded-card border border-gray-200 bg-white p-6 shadow-[0_0_31.6px_rgba(55,17,104,0.18)] max-sm:p-5 sm:p-8 md:max-h-[min(94dvh,800px)]"
        >
          <h2
            id="create-list-categories-popover-title"
            className="shrink-0 text-center font-lora text-xl font-semibold leading-snug text-gray-900 max-sm:text-lg"
          >
            Categories
          </h2>
          <div className="gt-keyboard-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain flex flex-col gap-8 pr-1 -mr-0.5 md:flex-none md:shrink-0 md:overflow-visible md:min-h-0">
              <section className="flex flex-col gap-3">
                <h3 className="font-lora font-semibold text-lg text-gray-900">Trip Type</h3>
                <div className="flex flex-wrap gap-2.5">
                  {TRIP_TYPES_UI.map((type) => {
                    const selected = pendingTripTypes.includes(type.id);
                    return (
                      <button
                        key={type.id}
                        type="button"
                        className={preferencePillClass(selected)}
                        style={preferencePillStyle(selected)}
                        onClick={() => togglePendingTripType(type.id)}
                      >
                        {type.icon}
                        <span>{type.label}</span>
                      </button>
                    );
                  })}
                </div>
              </section>

              <section className="flex flex-col gap-3">
                <h3 className="font-lora font-semibold text-lg text-gray-900">Travel With</h3>
                <div className="flex flex-wrap gap-2.5">
                  {TRAVEL_WITH_TYPES_UI.map((item) => {
                    const selected = pendingTravelWithTypes.includes(item.id);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={preferencePillClass(selected)}
                        style={preferencePillStyle(selected)}
                        onClick={() => togglePendingTravelWith(item.id)}
                      >
                        {item.icon}
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            </div>

            <div className="flex shrink-0 justify-end items-center gap-2.5 w-full pt-2 border-t border-gray-200">
              <button
                type="button"
                className="inline-flex items-center justify-center box-border min-h-9 px-2 border-none bg-transparent font-manrope text-[14px] font-bold leading-[1.4286] tracking-label text-orange-500 cursor-pointer hover:opacity-85 focus-visible:outline-2 focus-visible:outline-indigo-900 focus-visible:outline-offset-2"
                onClick={handleCategorySkip}
              >
                Cancel
              </button>
              <button
                type="button"
                className="inline-flex items-center justify-center box-border h-9 min-h-9 px-4 border-none rounded-button-md font-manrope text-[14px] font-bold leading-[1.4286] tracking-label transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-indigo-900 focus-visible:outline-offset-2 bg-orange-500 text-white cursor-pointer hover:bg-[#e04710]"
                onClick={handleCategoryUpdate}
              >
                Update
              </button>
            </div>
        </ResponsiveOverlay>
      )}
    </>
  );
};

export default CreateListModal;
